import React, { useState, useEffect } from 'react';
import { Container, Typography, Box, Avatar, Button, TextField, Dialog, DialogActions, DialogContent, DialogTitle, Snackbar, Alert, Grid, Card, CardContent, CardHeader, Collapse, IconButton, Accordion, AccordionSummary, AccordionDetails, List, ListItem, ListItemText } from '@mui/material';
import { amber, blue, green, deepPurple, orange } from '@mui/material/colors';
import FunctionsIcon from '@mui/icons-material/Functions';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ScienceIcon from '@mui/icons-material/Science';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import CollectionsIcon from '@mui/icons-material/Collections';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';

// helper functions
const summarizeExpressions = expressions => expressions.map(exp => {
    const initials = exp.expression
        .map(op => op.name.split(' ').map(w => w[0]).join('.') + '.')
        .join('');
    const count = exp.expression.length;
    const date = new Date(exp.date_created).toLocaleDateString();
    return { id: exp._id || exp.id, summary: `${initials}+${count} (${date})` };
});

// const summarizeAgents = agents => agents.map(agent => ({ 
//     id: agent.id, 
//     summary: agent.name 
// }));

// const summarizeSimulations = sims => sims.map(sim => ({
//     id: sim._id,
//     summary: `ID: ${sim._id.slice(0, 6)}..., Status: ${sim.status}`
// }));

async function summarizeSimulations(sims) {
    return sims.map(sim => {
        const id = sim._id;
        const status = sim.status;
        // Get the status symbol based on the status. draft, approved, rejected, completed.
        const statusSymbol = () => {
            if (status === 'draft') {
                return '⏳';
            } else if (status === 'completed') {
                return '🟢';
            } else if (status === 'approved') {
                return '✅';
            } else {
                return '❌';
            }
        };

        return { id, summary: `ID: ${id.slice(0, 6)}..., Status: ${statusSymbol()}` };
    });
}

async function summarizeAgents(agents) {
    return agents.map(agent => {
        const id = agent.id;
        const name = agent.name;
        const definitionMethod = agent.definitionMethod;
        const symbol = definitionMethod === 'crewAI' ? '🤖' : definitionMethod === 'expression' ? '🧬' : '📖';
        const raw = agent.avatar || '';
        // ensure proper data URL prefix for base64 avatars
        const avatarImg = raw.startsWith('data:') ? raw : raw ? `data:image/png;base64,${raw}` : '';
        return { id, summary: `${name}${symbol}`, avatar: avatarImg };
    });
}

async function summarizeExperiments(experiments, token) {
    const detailsArr = await Promise.all(
        experiments.map(exp => {
            const expId = exp._id || exp.id || exp.expt_id;
            return axios.get(`${API_URL}/api/experiment/${expId}/details`, { headers: { Authorization: `Bearer ${token}` } });
        })
    );
    return detailsArr.map((res, idx) => {
        const id = experiments[idx]._id || experiments[idx].id || experiments[idx].expt_id;
        const sessions = res.data.exptSessions?.length || res.data.sessionData?.length || 0;
        return { id, summary: `ID: ${id?.substring(0, 8) || 'Unknown'}..., Sessions: ${sessions}` };
    });
}

async function summarizeStimuliSets(stimuliSets) {
    return stimuliSets.map(stimuliSet => {
        const id = stimuliSet._id;
        const name = stimuliSet.name;
        const fileCount = stimuliSet.files?.length || 0;
        const dateCreated = new Date(stimuliSet.createdAt || stimuliSet.date).toLocaleDateString();
        return { 
            id, 
            summary: `${name} (${fileCount} files, ${dateCreated})`,
            name,
            fileCount,
            description: stimuliSet.description
        };
    });
}

// Get API base URL from environment variable
const API_URL = process.env.REACT_APP_API_ROOT || 'http://localhost:4000';

function Profile() {
    const { user, getAccessTokenSilently } = useAuth0();
    const [userData, setUserData] = useState(null);
    const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
    const [email, setEmail] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [alertOpen, setAlertOpen] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [alertSeverity, setAlertSeverity] = useState('success');

    // dashboard data states
    const [expressions, setExpressions] = useState([]);
    const [agents, setAgents] = useState([]);
    const [experiments, setExperiments] = useState([]);
    const [simulations, setSimulations] = useState([]);
    const [stimuliSets, setStimuliSets] = useState([]);
    // card expand states
    const [openExpr, setOpenExpr] = useState(false);
    const [openAgents, setOpenAgents] = useState(false);
    const [openExperiments, setOpenExperiments] = useState(false);
    const [openSims, setOpenSims] = useState(false);
    const [openStimuliSets, setOpenStimuliSets] = useState(false);

    useEffect(() => {
        if (user) {
            setEmail(user.email);                const fetchAllData = async () => {
                    try {
                        const token = await getAccessTokenSilently();
                        const [userRes, exprRes, agentsRes, expRes, simRes, stimuliRes] = await Promise.all([
                            axios.get(`${API_URL}/api/user`, { headers: { Authorization: `Bearer ${token}` } }),
                            axios.get(`${API_URL}/api/simulations/expression`, { headers: { Authorization: `Bearer ${token}` } }),
                            axios.get(`${API_URL}/api/agents`, { headers: { Authorization: `Bearer ${token}` } }),
                            axios.get(`${API_URL}/api/experiment`, { headers: { Authorization: `Bearer ${token}` } }),
                            axios.get(`${API_URL}/api/simulations/jobs`, { headers: { Authorization: `Bearer ${token}` } }),
                            axios.get(`${API_URL}/api/stimuli/sets`, { headers: { Authorization: `Bearer ${token}` } }),
                        ]);
                        const filteredAgents = agentsRes.data.filter(item => item.ownerId === user.sub);
                        const filteredExperiments = expRes.data.filter(item => item.ownerId === user.sub);
                        const filteredSims = simRes.data.filter(item => item.auth0ID === user.sub);
                        const filteredStimuliSets = stimuliRes.data.filter(item => item.ownerId === user.sub);

                        setUserData(userRes.data);
                        setExpressions(summarizeExpressions(exprRes.data));
                        setAgents(await summarizeAgents(filteredAgents));
                        setExperiments(await summarizeExperiments(filteredExperiments, token));
                        setSimulations(await summarizeSimulations(filteredSims));
                        setStimuliSets(await summarizeStimuliSets(filteredStimuliSets));
                    } catch (error) {
                        console.error('Error fetching data:', error);
                    }
                };
            fetchAllData();
        }
    }, [user, getAccessTokenSilently]);

    const handleOpenPasswordDialog = () => {
        setPasswordDialogOpen(true);
    };

    const handleClosePasswordDialog = () => {
        setPasswordDialogOpen(false);
        setNewPassword('');
        setConfirmPassword('');
    };

    const handlePasswordChange = async () => {
        // Validation
        if (newPassword !== confirmPassword) {
            setAlertSeverity('error');
            setAlertMessage('Passwords do not match');
            setAlertOpen(true);
            return;
        }

        if (newPassword.length < 8) {
            setAlertSeverity('error');
            setAlertMessage('Password must be at least 8 characters long');
            setAlertOpen(true);
            return;
        }

        try {
            console.log('Starting password change process');
            const token = await getAccessTokenSilently();
            console.log('Access token obtained');

            console.log('Changing password for:', email);
            console.log('Auth0 ID:', user.sub);
            
            // Call Auth0 Management API to change password
            console.log('Sending request to API:', `${API_URL}/api/change-password`);
            const response = await axios.post(`${API_URL}/api/change-password`, {
                email: email,
                newPassword: newPassword
            }, {
                headers: {
                    Authorization: `Bearer ${token}`,
                }
            });

            console.log('Password change response:', response.data);
            setAlertSeverity('success');
            setAlertMessage('Password changed successfully');
            setAlertOpen(true);
            handleClosePasswordDialog();
        } catch (error) {
            console.error('Error changing password:', error);
            
            // Log detailed error information
            if (error.response) {
                console.error('Error response status:', error.response.status);
                console.error('Error response data:', error.response.data);
            } else if (error.request) {
                console.error('No response received:', error.request);
            } else {
                console.error('Error setting up request:', error.message);
            }
            
            setAlertSeverity('error');
            setAlertMessage(error.response?.data?.message || error.response?.data?.error || 'Failed to change password');
            setAlertOpen(true);
        }
    };

    const handleCloseAlert = () => {
        setAlertOpen(false);
    };

    if (!user) {
        return (
            <Container sx={{ mt: 4 }}>
                <Typography variant="h6">No user data available</Typography>
            </Container>
        );
    }

    return (
        <Container sx={{ mt: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar src={user.picture} alt={user.name} sx={{ width: 64, height: 64, mr: 2 }} />
                <Typography variant="h5">{user.name}</Typography>
            </Box>
            <Typography variant="body1">
                <strong>Email:</strong> {user.email}
            </Typography>
            <Typography variant="body1">
                <strong>Sub:</strong> {user.sub}
            </Typography>
            <Typography variant="body1">
                <strong>Roles:</strong> {user['https://hax-platform.com/roles']?.join(', ')}
            </Typography>
            {user.nickname && (
                <Typography variant="body1">
                    <strong>Nickname:</strong> {user.nickname}
                </Typography>
            )}

            <Box sx={{ mt: 3, mb: 4 }}>
                <Button 
                    variant="contained" 
                    color="primary" 
                    onClick={handleOpenPasswordDialog}
                >
                    Change Password
                </Button>
            </Box>

            {/* Collapsible User Details */}
            <Accordion sx={{ mt: 4 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography>User Details</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <pre style={{ whiteSpace: 'pre-wrap' }}>{JSON.stringify(user, null, 2)}</pre>
                </AccordionDetails>
            </Accordion>

            {/* Collapsible Database Details */}
            {userData && (
                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography>Database Details</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography variant="body2"><strong>Role:</strong> {userData.role}</Typography>
                        <Typography variant="body2"><strong>Auth0 ID:</strong> {userData.auth0ID}</Typography>
                        <Typography variant="body2"><strong>Organization:</strong> {userData.org}</Typography>
                        <Typography variant="body2"><strong>Spending:</strong> ${userData.spending}</Typography>
                        <Typography variant="body2"><strong>Experiments:</strong> {userData.experiments?.join(', ')}</Typography>
                        <Typography variant="body2"><strong>Agents:</strong> {userData.agents?.join(', ')}</Typography>
                        <Typography variant="body2"><strong>Jobs:</strong> {userData.jobs?.length ? userData.jobs.join(', ') : 'None'}</Typography>
                        <Typography variant="body2"><strong>Created At:</strong> {new Date(userData.createdAt).toLocaleString()}</Typography>
                        <Typography variant="body2"><strong>Updated At:</strong> {new Date(userData.updatedAt).toLocaleString()}</Typography>
                    </AccordionDetails>
                </Accordion>
            )}

            {/* Dashboard */}
            <Box sx={{ mt: 4, px: 2 }}>
                <Typography variant="h6" gutterBottom>Your Dashboard</Typography>
                <Grid container spacing={2}>
                    {/* Expressions Card */}
                    <Grid item xs={12} sm={6} md={4}>
                        <Card elevation={3} sx={{ backgroundColor: amber[50] }}>
                            <CardHeader
                                avatar={<Avatar sx={{ bgcolor: amber[500] }}><FunctionsIcon/></Avatar>}
                                title="Expressions"
                                subheader={`${expressions.length} total`}
                                action={<IconButton onClick={() => setOpenExpr(!openExpr)}>{openExpr ? <ExpandLessIcon/> : <ExpandMoreIcon/>}</IconButton>}
                            />
                            <Collapse in={openExpr} timeout="auto" unmountOnExit>
                                <CardContent>
                                    {expressions.length ? (
                                        <List dense disablePadding>
                                            {expressions.map((item, idx) => (
                                                <ListItem key={item.id || `expression-${idx}`} sx={{ py: 0 }}>
                                                    <ListItemText primary={`${idx + 1}. ${item.summary}`} />
                                                </ListItem>
                                            ))}
                                        </List>
                                    ) : <Typography variant="body2">No expressions found</Typography>}
                                </CardContent>
                            </Collapse>
                        </Card>
                    </Grid>
                    {/* Agents Card */}
                    <Grid item xs={12} sm={6} md={4}>
                        <Card elevation={3} sx={{ backgroundColor: blue[50] }}>
                            <CardHeader
                                avatar={<Avatar sx={{ bgcolor: blue[500] }}><SmartToyIcon/></Avatar>}
                                title="Agents"
                                subheader={`${agents.length} total`}
                                action={<IconButton onClick={() => setOpenAgents(!openAgents)}>{openAgents ? <ExpandLessIcon/> : <ExpandMoreIcon/>}</IconButton>}
                            />
                            <Collapse in={openAgents} timeout="auto" unmountOnExit>
                                <CardContent>
                                    {agents.length ? (
                                        <List dense disablePadding>
                                            {agents.map((item, idx) => (
                                                <ListItem key={item.id || `agent-${idx}`} sx={{ py: 0, alignItems: 'center' }}>
                                                    <Avatar
                                                        src={item.avatar}
                                                        alt={item.summary}
                                                        sx={{ width: 35, height: 35, mr: 1, border: '2px solid rgba(0,0,0,0.1)' }}
                                                    />
                                                    <ListItemText primary={`${idx + 1}. ${item.summary}`} />
                                                </ListItem>
                                            ))}
                                        </List>
                                    ) : <Typography variant="body2">No agents found</Typography>}
                                </CardContent>
                            </Collapse>
                        </Card>
                    </Grid>
                    {/* Experiments Card */}
                    <Grid item xs={12} sm={6} md={4}>
                        <Card elevation={3} sx={{ backgroundColor: green[50] }}>
                            <CardHeader
                                avatar={<Avatar sx={{ bgcolor: green[500] }}><ScienceIcon/></Avatar>}
                                title="Experiments"
                                subheader={`${experiments.length} total`}
                                action={<IconButton onClick={() => setOpenExperiments(!openExperiments)}>{openExperiments ? <ExpandLessIcon/> : <ExpandMoreIcon/>}</IconButton>}
                            />
                            <Collapse in={openExperiments} timeout="auto" unmountOnExit>
                                <CardContent>
                                    {experiments.length ? (
                                        <List dense disablePadding>
                                            {experiments.map((item, idx) => (
                                                <ListItem key={item.id || `experiment-${idx}`} sx={{ py: 0 }}>
                                                    <ListItemText primary={`${idx + 1}. ${item.summary}`} />
                                                </ListItem>
                                            ))}
                                        </List>
                                    ) : <Typography variant="body2">No experiments found</Typography>}
                                </CardContent>
                            </Collapse>
                        </Card>
                    </Grid>
                    {/* Stimuli Sets Card */}
                    <Grid item xs={12} sm={6} md={4}>
                        <Card elevation={3} sx={{ backgroundColor: orange[50] }}>
                            <CardHeader
                                avatar={<Avatar sx={{ bgcolor: orange[500] }}><CollectionsIcon/></Avatar>}
                                title="Stimuli Sets"
                                subheader={`${stimuliSets.length} total`}
                                action={<IconButton onClick={() => setOpenStimuliSets(!openStimuliSets)}>{openStimuliSets ? <ExpandLessIcon/> : <ExpandMoreIcon/>}</IconButton>}
                            />
                            <Collapse in={openStimuliSets} timeout="auto" unmountOnExit>
                                <CardContent>
                                    {stimuliSets.length ? (
                                        <List dense disablePadding>
                                            {stimuliSets.map((item, idx) => (
                                                <ListItem key={item.id || `stimuli-set-${idx}`} sx={{ py: 0 }}>
                                                    <ListItemText 
                                                        primary={`${idx + 1}. ${item.name} (${item.fileCount} files)`}
                                                        secondary={item.description ? `${item.description.substring(0, 50)}${item.description.length > 50 ? '...' : ''}` : 'No description'}
                                                    />
                                                </ListItem>
                                            ))}
                                        </List>
                                    ) : <Typography variant="body2">No stimuli sets found</Typography>}
                                </CardContent>
                            </Collapse>
                        </Card>
                    </Grid>
                    {/* Simulations Card */}
                    <Grid item xs={12} sm={6} md={4}>
                        <Card elevation={3} sx={{ backgroundColor: deepPurple[50] }}>
                            <CardHeader
                                avatar={<Avatar sx={{ bgcolor: deepPurple[500] }}><PlayCircleOutlineIcon/></Avatar>}
                                title="Simulations"
                                subheader={`${simulations.length} total`}
                                action={<IconButton onClick={() => setOpenSims(!openSims)}>{openSims ? <ExpandLessIcon/> : <ExpandMoreIcon/>}</IconButton>}
                            />
                            <Collapse in={openSims} timeout="auto" unmountOnExit>
                                <CardContent>
                                    {simulations.length ? (
                                        <List dense disablePadding>
                                            {simulations.map((item, idx) => (
                                                <ListItem key={item.id || `simulation-${idx}`} sx={{ py: 0 }}>
                                                    <ListItemText primary={`${idx + 1}. ${item.summary}`} />
                                                </ListItem>
                                            ))}
                                        </List>
                                    ) : <Typography variant="body2">No simulations found</Typography>}
                                </CardContent>
                            </Collapse>
                        </Card>
                    </Grid>
                </Grid>
            </Box>

            {/* Password Change Dialog */}
            <Dialog open={passwordDialogOpen} onClose={handleClosePasswordDialog}>
                <DialogTitle>Change Password</DialogTitle>
                <DialogContent>
                    <TextField
                        margin="dense"
                        label="Email"
                        type="email"
                        fullWidth
                        value={email}
                        disabled
                    />
                    <TextField
                        margin="dense"
                        label="New Password"
                        type="password"
                        fullWidth
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        autoComplete="new-password"
                    />
                    <TextField
                        margin="dense"
                        label="Confirm New Password"
                        type="password"
                        fullWidth
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        autoComplete="new-password"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClosePasswordDialog} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={handlePasswordChange} color="primary">
                        Change Password
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Alert for feedback */}
            <Snackbar open={alertOpen} autoHideDuration={6000} onClose={handleCloseAlert}>
                <Alert onClose={handleCloseAlert} severity={alertSeverity}>
                    {alertMessage}
                </Alert>
            </Snackbar>
        </Container>
    );
}

export default Profile;